import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Box,
  Typography,
  Button,
  styled,
} from "@mui/material";
import { ReportProblem } from "@mui/icons-material";
import {
  useApproveNonMatchingAttributes,
  useFetchFailureAnalysis,
  useGetNonMatchingAttributes,
  useDeleteOuterHtml,
} from "../../../useQuery";
import { useNotification } from "../../../hooks";
import { DeleteIcon } from "../../../components-new/icons";
import { AuthedFileLink } from "../../../components-new/common";
import { useQueryClient } from "@tanstack/react-query";
import FoundElementsModal from "./FoundElementsModal";

const SynopContainer = styled(Box)`
  padding: 30px 25px;
  margin-bottom: 40px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0px 24px 23.3px 0px rgba(0, 0, 0, 0.03);
`;

const CardHeader = styled(Typography)`
  color: #000000;
  font-size: 16px;
  font-weight: 500;
`;

const ItemHeader = styled(Typography)`
  color: #8089a8;
  font-size: 13px;
  font-weight: 500;
`;

const ItemValue = styled(Typography)`
  color: #2b3539;
  font-size: 16px;
  font-weight: 400;
`;

const StyledTableCell = styled(TableCell)`
  color: #e0e0e0;
  font-size: 14px;
  font-weight: 600;
  border-bottom: 1px solid #e2e6f3;
  background: #2550d1;
`;

const StyledTableBodyCell = styled(TableCell)`
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
  color: #2b3539;
  font-size: 14px;
  font-weight: 500;
`;

const StepHistory = ({ currentEditItem, fromExcReports }) => {
  const notify = useNotification();
  const queryClient = useQueryClient();

  const tsrid = currentEditItem?.Test_Case_Results_Id;

  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [selectedElementHTML, setSelectedElementHTML] = useState(null);
  const rowsPerPage = 10;

  const { data, isLoading, isError, error } = useFetchFailureAnalysis(tsrid);

  const TSRid = data?.tableData?.[0]?.idtest_step_result;
  const teststepid = data?.tableData?.[0]?.Test_Step;

  const {
    data: nonMatchingData,
    isLoading: isNonMatchingLoading,
    isError: isNonMatchingError,
    error: nonMatchingError,
  } = useGetNonMatchingAttributes(teststepid, TSRid, {
    enabled: !!teststepid && !!TSRid,
  });

  const ptcrid = nonMatchingData?.ptcrid;
  const foundElements = nonMatchingData?.foundelements || [];

  useEffect(() => {
    if (
      nonMatchingData &&
      nonMatchingData.foundelements &&
      nonMatchingData.foundelements.length > 0
    ) {
      const firstElement = nonMatchingData.foundelements[0];
      const firstElementId = Object.keys(firstElement)[0];
      setSelectedElementId(firstElementId);
      setSelectedElementHTML(firstElement[firstElementId]);
    } else if (nonMatchingData && nonMatchingData.foundOuterHTML) {
      setSelectedElementHTML(nonMatchingData.foundOuterHTML);
    }
  }, [nonMatchingData]);

  const { mutate: approveAttributes, isLoading: isApproveLoading } =
    useApproveNonMatchingAttributes({
      onSuccess: (data) => {
        notify(data.message || "Attributes approved successfully", "success");
      },
      onError: (error) => {
        notify(error.message || "Failed to approve attributes", "error");
      },
    });

  const { mutate: deleteOuterHtml } = useDeleteOuterHtml({
    onSuccess: (data) => {
      notify(data.message || "Successfully deleted outer HTML", "success");
    },
    onError: (error) => {
      notify(error.message || "Failed to delete outer HTML", "error");
    },
  });

  const handleDeleteOuterHtml = (testCaseResultId, testStepResultId) => {
    deleteOuterHtml({ testCaseResultId, testStepResultId });
    queryClient.invalidateQueries(["failureAnalysis", tsrid]);
    queryClient.invalidateQueries(["nonMatchingAttributes", teststepid]);
  };

  const handleApproveClick = () => {
    if (selectedElementId) {
      approveAttributes({ teststepId: teststepid, elementId: selectedElementId });
    } else {
      notify("No element selected for approval", "error");
    }
  };

  const handleElementSelect = (elementId, elementHTML) => {
    setSelectedElementId(elementId);
    setSelectedElementHTML(elementHTML);
    setModalOpen(false);
  };

  const getStatusBackgroundColor = (status) => {
    const upperStatus = status.toUpperCase();
    if (upperStatus === "PASS") return "#e7fbe4";
    if (upperStatus === "FAIL") return "#fbe7e7";
    if (upperStatus === "ABEND") return "#eef4fc";
    return "#D3D3D3";
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  if (isLoading) return <Box p={3}>Loading...</Box>;
  if (isError) return <Box p={3}>Error: {error.message}</Box>;

  const tableData = data?.tableData || [];
  const paginatedData = tableData.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <Box>
      {/* Log Defect Button */}
      <Box display="flex" justifyContent="flex-end" mb={3}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<ReportProblem />}
          onClick={() => {}}
          sx={{
            textTransform: "none",
            padding: "10px 24px",
            fontSize: "14px",
            bgcolor: "#2550D1",
            "&:hover": {
              bgcolor: "#1e40af",
            },
          }}
        >
          Log Defect
        </Button>
      </Box>

      {/* Assessment and Action Section */}
      <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={3} mb={4}>
        <SynopContainer sx={{ flex: 1 }}>
          <Box mb={2}>
            <CardHeader>Our Assessment</CardHeader>
          </Box>
          {data?.ourAssessment && Object.keys(data.ourAssessment).length > 0 ? (
            <Box>
              <Box mb={2}>
                <ItemHeader>Issue Type</ItemHeader>
                <ItemValue>{data.ourAssessment.issueType}</ItemValue>
              </Box>
              <Box mb={2}>
                <ItemHeader>Issues</ItemHeader>
                <ItemValue>{data.ourAssessment.issues}</ItemValue>
              </Box>
              <Box mb={2}>
                <ItemHeader>Recommended Action</ItemHeader>
                <ItemValue>{data.recommended_action}</ItemValue>
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary" fontStyle="italic">
              No assessment data available at the moment.
            </Typography>
          )}
        </SynopContainer>

        <SynopContainer sx={{ flex: 1 }}>
          <Box mb={2}>
            <CardHeader>Recommended Action</CardHeader>
          </Box>
          {nonMatchingData ? (
            <Box>
              <Box mb={2}>
                <ItemHeader>Non-matching Attributes</ItemHeader>
                <ItemValue>{nonMatchingData.nonmatchingattr || "None"}</ItemValue>
              </Box>

              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <ItemHeader>Recorded HTML</ItemHeader>
                  <Box
                    sx={{
                      fontFamily: "monospace",
                      backgroundColor: "#f5f5f5",
                      padding: "8px",
                      borderRadius: "4px",
                      overflowX: "auto",
                      border: "1px solid #e0e0e0",
                      fontSize: "0.75rem",
                      maxHeight: "60px",
                      overflowY: "auto",
                    }}
                  >
                    {nonMatchingData.recordedOuterHTML || "No data available"}
                  </Box>
                </Box>

                <Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <ItemHeader>Found HTML</ItemHeader>
                    {foundElements.length > 1 && (
                      <Button
                        size="small"
                        variant="text"
                        color="primary"
                        onClick={() => setModalOpen(true)}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        View All ({foundElements.length})
                      </Button>
                    )}
                  </Box>
                  <Box
                    sx={{
                      fontFamily: "monospace",
                      backgroundColor: "#f5f5f5",
                      padding: "8px",
                      borderRadius: "4px",
                      overflowX: "auto",
                      border: "1px solid #e0e0e0",
                      fontSize: "0.75rem",
                      maxHeight: "60px",
                      overflowY: "auto",
                    }}
                  >
                    {selectedElementHTML || nonMatchingData.foundOuterHTML || "No data available"}
                  </Box>
                </Box>

                <Box>
                  <ItemHeader>Last Passed HTML</ItemHeader>
                  <Box
                    sx={{
                      fontFamily: "monospace",
                      backgroundColor: "#f5f5f5",
                      padding: "8px",
                      borderRadius: "4px",
                      overflowX: "auto",
                      border: "1px solid #e0e0e0",
                      fontSize: "0.75rem",
                      maxHeight: "60px",
                      overflowY: "auto",
                    }}
                  >
                    {nonMatchingData.lastPassedOuterHTML || "No data available"}
                  </Box>
                </Box>
              </Box>

              <Box mt={2} display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApproveClick}
                  disabled={isApproveLoading || !selectedElementId}
                  sx={{
                    textTransform: "capitalize",
                    padding: "8px 20px",
                    borderRadius: "6px",
                    fontSize: "14px",
                  }}
                >
                  Approve
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary" fontStyle="italic">
              No attribute data available at the moment.
            </Typography>
          )}
        </SynopContainer>
      </Box>

      {/* Found Elements Modal */}
      <FoundElementsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        foundElements={foundElements}
        selectedElementId={selectedElementId}
        onElementSelect={handleElementSelect}
      />

      {/* Results Table */}
      <SynopContainer>
        <Box mb={2}>
          <CardHeader>Execution History</CardHeader>
        </Box>
        <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid #e0e0e0" }}>
          <Table aria-label="failure analysis results">
            <TableHead>
              <TableRow>
                <StyledTableCell>Result ID</StyledTableCell>
                <StyledTableCell>Executed Date</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Screenshot</StyledTableCell>
                <StyledTableCell>Dom SnapShot</StyledTableCell>
                <StyledTableCell>Analysis Log</StyledTableCell>
                <StyledTableCell>Log File</StyledTableCell>
                <StyledTableCell>Element Found</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <TableRow key={row.idtest_step_result || index}>
                  <StyledTableBodyCell>{row.Test_Case_Results_Id}</StyledTableBodyCell>
                  <StyledTableBodyCell>{row.Executed_Date}</StyledTableBodyCell>
                  <StyledTableBodyCell>
                    <Box
                      sx={{
                        display: "inline-block",
                        minWidth: "82px",
                        padding: "8px 10px",
                        borderRadius: "41px",
                        backgroundColor: getStatusBackgroundColor(row.Status),
                        textAlign: "center",
                        textTransform: "capitalize",
                        color: "#2b3539",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      {row.Status}
                    </Box>
                  </StyledTableBodyCell>
                  <StyledTableBodyCell>
                    <AuthedFileLink
                      url={row.Failure_Screenshot_Location}
                      filename={`step-${row.Test_Case_Results_Id}-screenshot.png`}
                    >
                      Screenshot
                    </AuthedFileLink>
                  </StyledTableBodyCell>
                  <StyledTableBodyCell>
                    <AuthedFileLink
                      url={row.page_source_file}
                      filename={`step-${row.Test_Case_Results_Id}-dom.html`}
                    >
                      Dom Snapshot
                    </AuthedFileLink>
                  </StyledTableBodyCell>
                  <StyledTableBodyCell>
                    <AuthedFileLink
                      url={row.analysis_log_file}
                      filename={`step-${row.Test_Case_Results_Id}-analysis.log`}
                    >
                      Analysis Log
                    </AuthedFileLink>
                  </StyledTableBodyCell>
                  <StyledTableBodyCell>
                    <AuthedFileLink
                      url={row.logFile}
                      filename={`step-${row.Test_Case_Results_Id}.log`}
                    >
                      Log File
                    </AuthedFileLink>
                  </StyledTableBodyCell>
                  <StyledTableBodyCell sx={{ fontSize: "0.75rem", maxWidth: "300px" }}>
                    <Box
                      sx={{
                        maxHeight: "100px",
                        overflowY: "auto",
                        p: 1,
                        bgcolor: "#f5f5f5",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0",
                        display: "flex",
                        flexDirection: "row",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ flex: 1, overflowWrap: "break-word" }}>{row.outerhtml}</Box>
                      {row.outerhtml && ptcrid && row.Test_Case_Results_Id === ptcrid && (
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          endIcon={<DeleteIcon />}
                          onClick={() => {
                            handleDeleteOuterHtml(row.Test_Case_Results_Id, row.idtest_step_result);
                          }}
                          sx={{
                            textTransform: "capitalize",
                            fontSize: "12px",
                            minHeight: "32px",
                          }}
                        >
                          Delete
                        </Button>
                      )}
                    </Box>
                  </StyledTableBodyCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(tableData.length / rowsPerPage)}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      </SynopContainer>
    </Box>
  );
};

export default StepHistory;
